import React from 'react';

import { format } from 'date-fns';
import { ColumnsType } from 'antd/lib/table';
import { Button, Table, notification, Typography, Tag } from 'antd';
const { Text } = Typography;

import * as api from '../../api';
import config from '../../config';
import { RemoveButton, ReloadButton } from '../../components';
import { Account, CreditLineState, Manager } from '../../types';

import { Link } from 'react-router-dom';

type AccountsTableProps = {
  accounts: Account[];
  isAdmin: boolean | null;
  partnerId: number | null;
  page: number;
  size: number;
  total: number;
  fetchData(): Promise<void>;
  updateAccountList(
    accountId: number,
    creditLineAllocationConfigId: string | null,
    creditLineState: CreditLineState,
  ): void;
  onPaginationChanged(page: number, pageSize?: number | undefined): void;
};

const notificationReloadSuccess = (wabaName: string) => {
  notification['success']({
    message: `Update WABA -${wabaName}- Successfully`,
  });
};

const AccountsTable = ({
  accounts,
  isAdmin,
  partnerId,
  page,
  size,
  total,
  fetchData,
  updateAccountList,
  onPaginationChanged,
}: AccountsTableProps) => {
  const onClickEnableCreditLine = (accountId: number, managerId: number) => async () => {
    const manager = await api.manager.enableCreditLine(managerId);
    if (!manager) {
      return;
    }
    updateAccountList(accountId, manager.creditLineAllocationConfigId, manager.creditLineState);
  };
  const onClickDisableCreditLine = (accountId: number, managerId: number) => async () => {
    const manager = await api.manager.disableCreditLine(managerId);
    if (!manager) {
      return;
    }
    updateAccountList(accountId, manager.creditLineAllocationConfigId, manager.creditLineState);
  };

  const getTagElementByStatus = (status: string) => {
    switch (status.toLocaleLowerCase()) {
      case 'approved':
        return <Tag color="green">{status}</Tag>;
      case 'pending':
        return <Tag color="orange">{status}</Tag>;
      default:
        return <Tag color="red">{status}</Tag>;
    }
  };

  const columns: ColumnsType<Account> = [
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      width: 120,
      key: 'createdAt',
      align: 'center',
      render: function CreatedAt(createdAt) {
        return <>{format(new Date(createdAt), 'd MMM yyyy')}</>;
      },
    },
    {
      title: 'WABA',
      width: 200,
      key: 'waba',
      align: 'center',
      render: function WabaName({ name, status, wabaId }: Account) {
        return (
          <>
            <strong>{name}</strong><br/>
            {wabaId}<br/>
            {getTagElementByStatus(status)}
          </>
        );
      },
    },
    // {
    //   title: 'Owner Email',
    //   dataIndex: 'ownerEmail',
    //   key: 'ownerEmail',
    //   align: 'center',
    // },
    {
    
      title: 'User',
      key: 'ownerEmail',
      dataIndex: 'manager',
      width: 200,
      align: 'center',
          render: function Manager(manager: Manager, account: Account) {
        return <>
        {account.ownerEmail}<br/>
        <strong>Partner: </strong>
        {manager?.partner?.user?.email || 'Internal'}</>;
      },
    },
    {
      title: 'Business Name',
      dataIndex: 'businessName',
      key: 'businessName',
      width: 150,
      align: 'center',
    },
    {
      title: 'Phones',
      dataIndex: 'wabaId',
      key: 'phoneNumbers',
      width: 100,
      align: 'center',
      render: function PhoneNumber(wabaId: string) {
        return <Link to={{ pathname: `/phoneNumbers/${wabaId}` }}>More info</Link>;
      },
    },
  ];

  if (isAdmin || partnerId) {
    columns.push(
      {
        title: 'Credit Line Status',
        align: 'center',
        key: 'manager',
        width: 150,
        render: function CreditLineStatus(record: Account) {
          return (
            config.KEYREPLY_BUSINESS_ID !== record.businessId && (
              <div className="flex flex-jc-c flex-ai-c flex-dir-c">
                <Text>
                  {record?.manager?.creditLineState.toString().replace('_', ' ')}
                </Text>
                {record?.manager?.creditLineAllocationConfigId && (
                  <Button
                    type="primary"
                    danger
                    shape="round"
                    size="small"
                    onClick={onClickDisableCreditLine(record.id, record.manager.id)}
                  >
                    Disable
                  </Button>
                )}
                {record.manager && !record?.manager?.creditLineAllocationConfigId && (
                  <Button
                    type="primary"
                    shape="round"
                    size="small"
                    onClick={onClickEnableCreditLine(record.id, record.manager.id)}
                  >
                    Enable
                  </Button>
                )}
              </div>
            )
          );
        },
      },
      {
        title: 'Action',
        align: 'center',
        width: 100,
        key: 'tools',
        render: function Tools(record: Account) {
          const handleRemove = async () => {
            try {
              const isRemove = await api.account.remove(record.id);
              if (isRemove) {
                await fetchData();
              }
            } catch (error) {
              throw error;
            }
          };
          const handleReload = async () => {
            try {
              const isReload = await api.account.reload(record.wabaId);
              if (isReload) {
                await fetchData();
                notificationReloadSuccess(record.name);
              }
            } catch (error) {
              throw error;
            }
          };
          return (
            <div className="flex flex-jc-c flex-ai-c ">
              <div className={isAdmin ? 'mr-10' : ''}>
                <ReloadButton handleReload={handleReload} uniqueValue={record.name} />
              </div>
              {isAdmin && <RemoveButton handleRemove={handleRemove} uniqueValue={record.name} />}
            </div>
          );
        },
      },
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={accounts.map((acc) => {
        return { ...acc, key: acc.id };
      })}
      bordered
      pagination={{
        defaultCurrent: 1,
        current: page,
        total: total,
        showSizeChanger: true,
        defaultPageSize: 10,
        pageSize: size,
        position: ['bottomRight'],
        size: 'small',
        onChange: (page: number, pageSize?: number | undefined) => onPaginationChanged(page, pageSize),
      }}
      // sticky
      scroll={{ x: true }}
    />
  );
};

export default AccountsTable;
